import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import {
  doc, collection, onSnapshot, setDoc, updateDoc, deleteDoc, addDoc,
  getDoc, getDocs,
} from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../firebase';
import { writeDoc, updateDocFields, deleteDocument, addToCollection } from '../lib/api';
import { randomTrack, SCENE_MAP } from '../lib/ambience';
import { validateOffer, applyTradeToPlayer } from '../lib/trades';
import { resolveOutcome as resolveChallengeOutcome } from '../lib/minigames';
import { sanitizeForBackup } from '../lib/characterIO';
import { equipItem as applyEquipSlots, computeAc } from '../lib/equip';
import { rollDice } from '../lib/dice';
import {
  INVENTORY_CATALOG, catalogLookup, totalInCp,
  cpToCoins, applyCoinTransaction, addItemStrict, currentWeight, carryCapacity,
} from '../lib/inventory';
import { BUFF_MAP } from '../lib/buffs';
import { resolveDiscoveries, passivePerception } from '../lib/perception';
import { describeShopItem, getShopItem } from '../lib/shops';

const SNAPSHOT_DEBOUNCE_MS  = 5000;
const SNAPSHOT_HISTORY_KEEP = 10;

const CampaignContext = createContext(null);

// ── Token colour palette ───────────────────────────────────────────────────
const TOKEN_COLOURS = [
  '#c084fc', '#60a5fa', '#34d399', '#fb923c',
  '#f472b6', '#facc15', '#38bdf8', '#a78bfa',
];
let colourIdx = 0;
function nextColour() {
  const c = TOKEN_COLOURS[colourIdx % TOKEN_COLOURS.length];
  colourIdx++;
  return c;
}

// Stringify a player record while ignoring presence-only fields. Used to
// detect snapshots where only `lastSeen` (10s heartbeat) changed so we can
// skip the re-render cascade through CampaignContext.
function playerSignature(p) {
  return JSON.stringify(p, (k, v) => k === 'lastSeen' ? undefined : v);
}

export function CampaignProvider({ children }) {
  const [tokens,     setTokens]     = useState({});
  const [players,    setPlayers]    = useState({});
  const [pings,      setPings]      = useState({});
  const [diceRolls,  setDiceRolls]  = useState({});
  const [trades,     setTrades]     = useState({});
  const [challenges, setChallenges] = useState({});
  const [traps,                setTraps]                = useState({});
  const [perceptionPrompts,    setPerceptionPrompts]    = useState({});
  const [trapReactions,        setTrapReactions]        = useState({});
  const [teleports,            setTeleports]            = useState({});
  const [initiative, setInitiative] = useState({ turnOrder: [], activeIndex: 0, round: 1, mode: 'roll' });
  const [campaign,   setCampaign]   = useState(null);
  const [musicState, setMusicState] = useState({
    scene: null, playing: false, volume: 0.5, trackUrl: null,
    loopMode: 'playlist', playlistIndex: 0,
    previousScene: null, previousTrackUrl: null,
  });
  const musicStateRef = useRef(musicState);
  useEffect(() => { musicStateRef.current = musicState; }, [musicState]);

  useEffect(() => {
    const unsubs = [];

    unsubs.push(onSnapshot(doc(db, 'campaigns', CAMPAIGN_ID), (snap) => {
      if (snap.exists()) setCampaign({ id: snap.id, ...snap.data() });
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'tokens'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setTokens(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'players'), (snap) => {
      // 10s heartbeat writes `lastSeen` to every player doc. Naively
      // setPlayers(next) would trip every downstream consumer on each tick.
      // Reuse the previous record reference when nothing but `lastSeen`
      // changed, and reuse the whole players object if no record changed.
      setPlayers((prev) => {
        const next = {};
        let anyChange = false;
        snap.forEach((d) => {
          const newData = { id: d.id, ...d.data() };
          const prevData = prev[d.id];
          if (prevData && playerSignature(prevData) === playerSignature(newData)) {
            next[d.id] = prevData;
          } else {
            next[d.id] = newData;
            anyChange = true;
          }
        });
        // Catch deletions.
        for (const id in prev) if (!(id in next)) anyChange = true;
        return anyChange ? next : prev;
      });
    }));

    unsubs.push(onSnapshot(doc(db, 'campaigns', CAMPAIGN_ID, 'initiative', 'order'), (snap) => {
      if (snap.exists()) setInitiative(snap.data());
    }));

    unsubs.push(onSnapshot(doc(db, 'campaigns', CAMPAIGN_ID, 'music', 'state'), (snap) => {
      if (snap.exists()) setMusicState(snap.data());
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'pings'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setPings(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'rolls'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setDiceRolls(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'trades'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setTrades(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'challenges'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setChallenges(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'traps'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setTraps(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'perceptionPrompts'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setPerceptionPrompts(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'trapReactions'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setTrapReactions(next);
    }));

    unsubs.push(onSnapshot(collection(db, 'campaigns', CAMPAIGN_ID, 'teleports'), (snap) => {
      const next = {};
      snap.forEach((d) => { next[d.id] = { id: d.id, ...d.data() }; });
      setTeleports(next);
    }));

    return () => unsubs.forEach((u) => u());
  }, []);

  // ── Token actions ────────────────────────────────────────────────────────

  const spawnToken = useCallback(async ({ name, type = 'monster', colour, size = 1, x, y, hp, maxHp, initiativeBonus, ac, speed, visionRange, cr, objectType, passable, rotation, class: charClass, level, hidden, onIntersection, paperdoll, combatStyle, packKey, ownerId, sizeCategory }) => {
    const col   = colour || nextColour();
    const tokenData = {
      type,
      name,
      x: x ?? 0, y: y ?? 0,
      hp:             hp      ?? 10,
      maxHp:          maxHp   ?? hp ?? 10,
      tempHp:         0,
      size,
      speed:          speed   ?? 30,
      visionRange:    visionRange ?? 0,
      initiativeBonus: initiativeBonus ?? 0,
      ac:             ac      ?? 10,
      cr:             cr      ?? null,
      objectType:     objectType ?? null,
      passable:       passable ?? false,
      rotation:       rotation ?? 0,
      class:          charClass ?? null,
      level:          level ?? 1,
      colour: col,
      conditions: [],
      buffs: [],
      actionStates: [],
      hidden:        hidden ?? false,
      defeated: false,
      concentration: false,
      onIntersection: onIntersection ?? false,
      paperdoll:     paperdoll ?? null,
      combatStyle:   combatStyle ?? null,
      packKey:       packKey ?? null,
      // ownerId in the create-write so a player-spawned hidden token appears in
      // the DM's snapshot already linked to its player — no orphan window.
      ownerId:       ownerId ?? null,
      sizeCategory:  sizeCategory ?? null,
    };
    const id = await addToCollection(`campaigns/${CAMPAIGN_ID}/tokens`, tokenData);
    return id;
  }, []);

  const moveToken = useCallback(async (tokenId, x, y) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`, { x, y });
  }, []);

  // Faithful copy of an existing token with optional overrides. Strips id and
  // ownership/turn-state fields that shouldn't follow a paste, but preserves
  // every other field on the source — sprite, paperdoll, packKey, sizeCategory,
  // rotation, custom flags, etc. — without requiring spawnToken to know them.
  const cloneToken = useCallback(async (sourceTokenId, overrides = {}) => {
    const ref = doc(db, 'campaigns', CAMPAIGN_ID, 'tokens', sourceTokenId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const { id: _id, ownerId: _ownerId, mirrorGroupId: _mg, mirrorDuplicate: _md, linkedTo: _lt,
            movementUsed: _mu, deathSaves: _ds, ...rest } = snap.data();
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/tokens`, { ...rest, ...overrides });
  }, []);

  const updateToken = useCallback(async (tokenId, fields) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`, fields);
  }, []);

  // ── Music actions ────────────────────────────────────────────────────────

  const saveMusicState = useCallback(async (data) => {
    await writeDoc(`campaigns/${CAMPAIGN_ID}/music/state`, data);
  }, []);

  // ── Initiative actions ───────────────────────────────────────────────────

  const saveInitiative = useCallback(async (data) => {
    await writeDoc(`campaigns/${CAMPAIGN_ID}/initiative/order`, data);
  }, []);

  const removeToken = useCallback(async (tokenId) => {
    // Auto-strip from initiative if present
    const order = initiative.turnOrder || [];
    if (order.includes(tokenId)) {
      const idx = order.indexOf(tokenId);
      const newOrder = order.filter((id) => id !== tokenId);
      let newIndex = initiative.activeIndex;
      // If removed token was before current turn, shift index back
      if (idx < newIndex) {
        newIndex = newIndex - 1;
      }
      // Clamp to valid range
      newIndex = Math.min(newIndex, Math.max(0, newOrder.length - 1));
      await saveInitiative({ ...initiative, turnOrder: newOrder, activeIndex: newIndex });
    }
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`);
  }, [initiative, saveInitiative]);

  // ── Start rolling phase ────────────────────────────────────────────────
  const startRolling = useCallback(async (tokenIds) => {
    // Build pending rolls map for selected tokens, pre-fill with null
    const pending = {};
    const ids = tokenIds || Object.values(tokens).filter((t) => !t.defeated && t.type !== 'object').map((t) => t.id);
    ids.forEach((id) => { pending[id] = null; });
    await saveInitiative({
      phase:        'rolling',
      pendingRolls: pending,
      turnOrder:    [],
      activeIndex:  0,
      round:        1,
      mode:         'roll',
      battleScreen: false,
    });
  }, [tokens, saveInitiative]);

  // ── Player/DM submits a roll ───────────────────────────────────────────
  const submitRoll = useCallback(async (tokenId, roll) => {
    const current = initiative.pendingRolls || {};
    const token   = tokens[tokenId];
    const total   = parseInt(roll, 10) + (token?.initiativeBonus || 0);
    await saveInitiative({
      ...initiative,
      pendingRolls: { ...current, [tokenId]: { roll: parseInt(roll, 10), total } },
    });
  }, [initiative, tokens, saveInitiative]);

  // ── DM begins battle — sort rolls, show battle screen ─────────────────
  const beginBattle = useCallback(async () => {
    const pending = initiative.pendingRolls || {};
    const entries = Object.entries(pending)
      .filter(([, v]) => v !== null)
      .map(([tokenId, v]) => ({ tokenId, total: v?.total ?? 0 }))
      .sort((a, b) => b.total - a.total);

    await saveInitiative({
      phase:        'active',
      pendingRolls: {},
      turnOrder:    entries.map((e) => e.tokenId),
      activeIndex:  0,
      round:        1,
      mode:         'roll',
      battleScreen: true, // triggers dramatic screen on Display + Player views
    });

    // Auto-play a random battle track in playlist loop mode
    const ms = musicStateRef.current;
    const battleTrack = randomTrack('battle');
    const battleScene = SCENE_MAP['battle'];
    const battleIdx = battleScene ? battleScene.tracks.indexOf(battleTrack) : 0;
    await saveMusicState({
      scene: 'battle', playing: true, volume: ms.volume,
      trackUrl: battleTrack,
      loopMode: 'playlist', playlistIndex: battleIdx >= 0 ? battleIdx : 0,
      previousScene: ms.scene, previousTrackUrl: ms.trackUrl, nextScene: null,
    });

    // Auto-clear the battle screen flag after 4 seconds
    setTimeout(async () => {
      await saveInitiative({
        ...initiative,
        phase:        'active',
        pendingRolls: {},
        turnOrder:    entries.map((e) => e.tokenId),
        activeIndex:  0,
        round:        1,
        battleScreen: false,
      });
    }, 4000);
  }, [initiative, saveInitiative, saveMusicState]);

  const nextTurn = useCallback(async () => {
    // Filter out any ghost IDs (tokens deleted outside initiative flow)
    const liveOrder = initiative.turnOrder.filter((id) => tokens[id]);
    if (!liveOrder.length) return;

    // If the order changed, persist and re-clamp index
    let currentIndex = initiative.activeIndex;
    if (liveOrder.length !== initiative.turnOrder.length) {
      currentIndex = Math.min(currentIndex, liveOrder.length - 1);
    }

    let nextIndex = currentIndex + 1;
    let nextRound = initiative.round;
    if (nextIndex >= liveOrder.length) {
      nextIndex = 0;
      nextRound = initiative.round + 1;
    }

    // Auto-skip defeated tokens
    let loopGuard = liveOrder.length;
    while (loopGuard-- > 0 && tokens[liveOrder[nextIndex]]?.defeated) {
      nextIndex++;
      if (nextIndex >= liveOrder.length) {
        nextIndex = 0;
        nextRound++;
      }
    }

    // Tick buff durations for the token whose turn is STARTING
    const upcomingTokenId = liveOrder[nextIndex];
    const upcomingToken   = upcomingTokenId ? tokens[upcomingTokenId] : null;
    if (upcomingToken && upcomingToken.buffs?.length) {
      const updatedBuffs = upcomingToken.buffs
        .map((b) => b.permanent ? b : { ...b, duration: b.duration - 1 })
        .filter((b) => b.permanent || b.duration > 0);
      if (updatedBuffs.length !== upcomingToken.buffs.length) {
        // Check if a mirror_image buff just expired — clean up duplicate tokens
        const expiredMirror = upcomingToken.buffs.find(
          (b) => b.id === 'mirror_image' && !updatedBuffs.some((u) => u.id === 'mirror_image')
        );
        const updates = { buffs: updatedBuffs };
        if (expiredMirror?.mirrorGroupId) {
          const dupes = Object.values(tokens).filter(
            (t) => t.mirrorDuplicate && t.mirrorGroupId === expiredMirror.mirrorGroupId
          );
          await Promise.all(dupes.map((t) => deleteDocument(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`)));
          updates.mirrorGroupId = null;
        }
        await updateDocFields(
          `campaigns/${CAMPAIGN_ID}/tokens/${upcomingTokenId}`,
          updates
        );
      }
    }

    // Reset movement budget on all tokens that moved or dashed this turn
    const movedTokens = Object.values(tokens).filter((t) => t.movementUsed || t.dashed);
    await Promise.all(movedTokens.map((t) =>
      updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`, { movementUsed: 0, dashed: false })
    ));

    await saveInitiative({ ...initiative, turnOrder: liveOrder, activeIndex: nextIndex, round: nextRound });
  }, [initiative, tokens, saveInitiative]);

  const prevTurn = useCallback(async () => {
    if (!initiative.turnOrder.length) return;
    let prevIndex = initiative.activeIndex - 1;
    let prevRound = initiative.round;
    if (prevIndex < 0) {
      prevIndex = initiative.turnOrder.length - 1;
      prevRound = Math.max(1, initiative.round - 1);
    }
    await saveInitiative({ ...initiative, activeIndex: prevIndex, round: prevRound });
  }, [initiative, saveInitiative]);

  const addToInitiative = useCallback(async (tokenId) => {
    if (initiative.turnOrder.includes(tokenId)) return;
    await saveInitiative({ ...initiative, turnOrder: [...initiative.turnOrder, tokenId] });
  }, [initiative, saveInitiative]);

  const removeFromInitiative = useCallback(async (tokenId) => {
    const newOrder  = initiative.turnOrder.filter((id) => id !== tokenId);
    const newIndex  = Math.min(initiative.activeIndex, Math.max(0, newOrder.length - 1));
    await saveInitiative({ ...initiative, turnOrder: newOrder, activeIndex: newIndex });
  }, [initiative, saveInitiative]);

  // Simple cancel — just reset initiative to inactive, no music or cleanup
  const cancelInitiative = useCallback(async () => {
    const movedTokens = Object.values(tokens).filter((t) => t.movementUsed || t.dashed);
    await Promise.all(movedTokens.map((t) =>
      updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`, { movementUsed: 0, dashed: false })
    ));
    await saveInitiative({ phase: 'inactive', turnOrder: [], activeIndex: 0, round: 1, pendingRolls: {}, mode: 'roll', battleScreen: false, combatEndScreen: false, combatOutcome: null });
  }, [saveInitiative, tokens]);

  const clearInitiative = useCallback(async (outcome = 'victory') => {
    // Remove defeated monsters/NPCs and mirror image duplicates from the map
    const defeated = Object.values(tokens).filter((t) => t.type !== 'player' && t.type !== 'object' && t.defeated);
    const mirrorDupes = Object.values(tokens).filter((t) => t.mirrorDuplicate);
    const movedTokens = Object.values(tokens).filter((t) => t.movementUsed || t.dashed);
    await Promise.all([
      ...defeated.map((t) => deleteDocument(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`)),
      ...mirrorDupes.map((t) => deleteDocument(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`)),
      ...movedTokens.map((t) => updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`, { movementUsed: 0, dashed: false })),
    ]);
    // Clear mirrorGroupId from any tokens that had it
    const mirrorSources = Object.values(tokens).filter((t) => t.mirrorGroupId && !t.mirrorDuplicate);
    await Promise.all(mirrorSources.map((t) => {
      const cleaned = (t.buffs || []).filter((b) => b.id !== 'mirror_image');
      return updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${t.id}`, { mirrorGroupId: null, buffs: cleaned });
    }));

    // Auto-play victory or defeat music (play once then stop)
    const ms = musicStateRef.current;
    const musicScene = outcome === 'defeat' ? 'defeat' : 'victory';
    await saveMusicState({
      scene: musicScene, playing: true, volume: ms.volume,
      trackUrl: randomTrack(musicScene),
      loopMode: 'none', playlistIndex: 0,
      previousScene: null, previousTrackUrl: null, nextScene: null,
    });

    // Show combat end screen briefly
    await saveInitiative({ phase: 'inactive', turnOrder: [], activeIndex: 0, round: 1, pendingRolls: {}, mode: 'roll', battleScreen: false, combatEndScreen: true, combatOutcome: outcome });
    setTimeout(async () => {
      await saveInitiative({ phase: 'inactive', turnOrder: [], activeIndex: 0, round: 1, pendingRolls: {}, mode: 'roll', battleScreen: false, combatEndScreen: false, combatOutcome: null });
    }, 4000);
  }, [saveInitiative, saveMusicState, tokens]);

  // ── Token assignment ──────────────────────────────────────────────────────
  const assignToken = useCallback(async (tokenId, playerId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`, { ownerId: playerId || null });
    if (playerId) {
      await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, { tokenId });
    }
  }, []);

  // Per-player debounced snapshot scheduler. The snapshot writes the full
  // sanitized record into `playerBackups/<id>/snapshots/<ts>` 5s after the
  // last edit settles. Bursts of edits (e.g., toggling several spell-slot
  // pips) collapse into a single snapshot.
  const playerSnapshotTimersRef = useRef({});
  const playersRefForBackup     = useRef(players);
  useEffect(() => { playersRefForBackup.current = players; }, [players]);

  const writeSnapshot = useCallback(async (playerId) => {
    const record = playersRefForBackup.current?.[playerId];
    if (!record) return;
    const sanitized = sanitizeForBackup(record);
    const ts = Date.now();
    const snapDocPath = `campaigns/${CAMPAIGN_ID}/playerBackups/${playerId}/snapshots/${ts}`;
    await writeDoc(snapDocPath, {
      at:       ts,
      charName: sanitized?.charName || null,
      class:    sanitized?.class    || null,
      level:    sanitized?.level    || null,
      record:   sanitized,
    });
    // Prune older snapshots beyond the keep window.
    const allSnaps = await getDocs(collection(db, 'campaigns', CAMPAIGN_ID, 'playerBackups', playerId, 'snapshots'));
    const sorted = allSnaps.docs
      .map((d) => ({ id: d.id, at: d.data()?.at || 0 }))
      .sort((a, b) => b.at - a.at);
    const overflow = sorted.slice(SNAPSHOT_HISTORY_KEEP);
    await Promise.all(overflow.map((s) =>
      deleteDocument(`campaigns/${CAMPAIGN_ID}/playerBackups/${playerId}/snapshots/${s.id}`)
    ));
  }, []);

  const scheduleSnapshot = useCallback((playerId) => {
    const timers = playerSnapshotTimersRef.current;
    if (timers[playerId]) clearTimeout(timers[playerId]);
    timers[playerId] = setTimeout(() => {
      delete timers[playerId];
      writeSnapshot(playerId).catch(() => {});
    }, SNAPSHOT_DEBOUNCE_MS);
  }, [writeSnapshot]);

  const updatePlayer = useCallback(async (playerId, fields) => {
    // Heartbeat-only writes (lastSeen / connected / attentive) must NOT
    // resurrect a doc that was just deleted by the DM. updateDoc throws on
    // missing docs — swallow that silently so the deletion sticks.
    // Anything else is character-meaningful: setDoc-merge so first-create
    // from CharacterSetup still works.
    const onlyHeartbeat = Object.keys(fields).every((k) =>
      k === 'lastSeen' || k === 'connected' || k === 'attentive'
    );
    if (onlyHeartbeat) {
      try {
        await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, fields);
      } catch { /* doc was deleted — leave it gone */ }
      return;
    }
    await writeDoc(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, fields);
    scheduleSnapshot(playerId);
  }, [scheduleSnapshot]);

  // Restore a previously captured snapshot back onto the live player record.
  // Used by the DM-side history panel; snapshot id is the timestamp string.
  const restorePlayerSnapshot = useCallback(async (playerId, snapshotId) => {
    const ref = doc(db, 'campaigns', CAMPAIGN_ID, 'playerBackups', playerId, 'snapshots', snapshotId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const record = snap.data()?.record;
    if (!record) return;
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, record);
    scheduleSnapshot(playerId);
  }, [scheduleSnapshot]);

  const deletePlayer = useCallback(async (playerId) => {
    // Cancel any pending snapshot debounce so it doesn't fire after we delete.
    const timers = playerSnapshotTimersRef.current;
    if (timers[playerId]) { clearTimeout(timers[playerId]); delete timers[playerId]; }
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/players/${playerId}`);
  }, []);

  // ── Equipment + consumables ──────────────────────────────────────────────
  // Toggle an item's `equipped` flag with proper slot exclusivity, and
  // re-derive AC from the next inventory state. AC is written to BOTH the
  // player record and the player's token so DM TokenInspector reflects it.
  // `targetSlot` is optional — pass 'offHand' to put a one-handed weapon
  // in the off-hand instead of its default slot.
  const equipItemInInventory = useCallback(async (playerId, itemId, targetSlot) => {
    const player = playersRefForBackup.current?.[playerId];
    if (!player) return;
    // No hard gate — players can equip anything. Penalties surface visibly
    // via equipPenalty() in the EquipTab; the DM enforces consequences.
    const nextInventory = applyEquipSlots(player.inventory || [], itemId, targetSlot);
    const nextRecord = { ...player, inventory: nextInventory };
    const nextAc = computeAc(nextRecord);
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, {
      inventory: nextInventory,
      ac:        nextAc,
    });
    // Also reflect AC on the owned player token, if any.
    if (player.tokenId) {
      const tokenRef = doc(db, 'campaigns', CAMPAIGN_ID, 'tokens', player.tokenId);
      const tokenSnap = await getDoc(tokenRef);
      if (tokenSnap.exists()) {
        await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${player.tokenId}`, { ac: nextAc });
      }
    }
  }, []);

  // Use a consumable item: heal | apply buff | (null = ask DM, no-op).
  // Decrements item qty (deletes entry at 0). Heal rolls broadcast to the
  // dice overlay so the table can see the result; buff appends to token.buffs.
  const useConsumable = useCallback(async (playerId, tokenId, itemId) => {
    const player = playersRefForBackup.current?.[playerId];
    if (!player) return;
    const item = (player.inventory || []).find((i) => i.id === itemId);
    if (!item) return;
    const cat = INVENTORY_CATALOG.find((c) => c.category === item.category && c.name === item.name);
    const useSpec = cat?.use;
    if (!useSpec || useSpec.type === null) return; // ask-DM items: caller can show a toast

    // Decrement qty (or remove the row).
    const nextQty = (item.qty || 0) - 1;
    const nextInventory = nextQty > 0
      ? player.inventory.map((i) => i.id === itemId ? { ...i, qty: nextQty } : i)
      : player.inventory.filter((i) => i.id !== itemId);

    if (useSpec.type === 'heal' && tokenId) {
      // Parse '2d4' → {sides:4, count:2}
      const m = (useSpec.dice || '1d4').match(/^(\d*)d(\d+)$/);
      const count = m ? (parseInt(m[1], 10) || 1) : 1;
      const sides = m ? parseInt(m[2], 10) : 4;
      const result = rollDice({ dice: [{ sides, count }], modifier: useSpec.bonus || 0, mode: 'normal' });
      const healAmount = result.total;
      // Apply heal to token.
      const tokenSnap = await getDoc(doc(db, 'campaigns', CAMPAIGN_ID, 'tokens', tokenId));
      if (tokenSnap.exists()) {
        const t = tokenSnap.data();
        const nextHp = Math.min((t.maxHp || 0), (t.hp || 0) + healAmount);
        await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`, { hp: nextHp });
      }
      // Broadcast the roll so Display TV / RollLog see it.
      await addToCollection(`campaigns/${CAMPAIGN_ID}/rolls`, {
        ...result,
        playerId,
        playerName: player.charName || 'Player',
        colour:     player.colour    || '#5cb870',
        isDm:       false,
        label:      item.name,
        createdAt:  Date.now(),
      });
    } else if (useSpec.type === 'buff' && tokenId) {
      const def = BUFF_MAP[useSpec.buffId];
      if (def) {
        const newBuff = {
          id:     def.id,
          label:  def.label,
          icon:   def.icon,
          color:  def.color,
          duration: useSpec.duration ?? null,
          permanent: !useSpec.duration,
          concentration: !!def.concentration,
        };
        const tokenSnap = await getDoc(doc(db, 'campaigns', CAMPAIGN_ID, 'tokens', tokenId));
        if (tokenSnap.exists()) {
          const buffs = tokenSnap.data().buffs || [];
          await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${tokenId}`, {
            buffs: [...buffs, newBuff],
          });
        }
      }
    }

    // Persist the inventory decrement.
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, { inventory: nextInventory });
    scheduleSnapshot(playerId);
  }, [scheduleSnapshot]);

  // ── Pings ────────────────────────────────────────────────────────────────
  const createPing = useCallback(async (playerId, x, y, colour) => {
    await addToCollection(`campaigns/${CAMPAIGN_ID}/pings`, {
      playerId, x, y, colour: colour || '#60a5fa', createdAt: Date.now(),
    });
  }, []);

  const deletePing = useCallback(async (id) => {
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/pings/${id}`);
  }, []);

  // ── Dice rolls ───────────────────────────────────────────────────────────
  // Fire-and-forget broadcast: write the roll record; clients listen via the
  // diceRolls snapshot, animate fresh entries, and prune old ones from the
  // log via deleteDiceRoll once they age out.
  const submitDiceRoll = useCallback(async (record) => {
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/rolls`, {
      ...record,
      createdAt: Date.now(),
    });
  }, []);

  const deleteDiceRoll = useCallback(async (id) => {
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/rolls/${id}`);
  }, []);

  // Mark a roll as dismissed without removing it from the log. Only the
  // roller (or DM) is supposed to call this; broadcasts everywhere so the
  // overlay disappears on every connected device.
  const dismissDiceRoll = useCallback(async (id) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/rolls/${id}`, { dismissedAt: Date.now() });
  }, []);

  // ── Trades ───────────────────────────────────────────────────────────────
  // Player-to-player trades. The accept action runs validation against the
  // current Firestore-backed `players` state and writes both records + the
  // trade status. Race conditions are accepted (6-player table); validate
  // catches the dominant case (sender modified inventory mid-trade).
  const proposeTrade = useCallback(async (record) => {
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/trades`, record);
  }, []);

  const cancelTrade = useCallback(async (tradeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
      status: 'cancelled', resolvedAt: Date.now(),
    });
  }, []);

  const declineTrade = useCallback(async (tradeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
      status: 'declined', resolvedAt: Date.now(),
    });
  }, []);

  // Snapshot players so the validation/apply step uses a consistent view.
  // Imports the pure functions from lib/trades to keep this thin.
  const playersRef = useRef(players);
  useEffect(() => { playersRef.current = players; }, [players]);
  const tradesRef = useRef(trades);
  useEffect(() => { tradesRef.current = trades; }, [trades]);

  const acceptTrade = useCallback(async (tradeId) => {
    const trade = tradesRef.current[tradeId];
    if (!trade || trade.status !== 'pending') return;

    const sender = playersRef.current[trade.fromPlayerId];
    const recipient = playersRef.current[trade.toPlayerId];
    if (!sender || !recipient) {
      await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
        status: 'failed', failedReason: 'Player record missing', resolvedAt: Date.now(),
      });
      return;
    }

    const senderCheck = validateOffer(sender, trade.fromOffer);
    if (!senderCheck.ok) {
      await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
        status: 'failed', failedReason: senderCheck.reason, resolvedAt: Date.now(),
      });
      return;
    }
    const recipientCheck = validateOffer(recipient, trade.toOffer);
    if (!recipientCheck.ok) {
      await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
        status: 'failed', failedReason: recipientCheck.reason, resolvedAt: Date.now(),
      });
      return;
    }

    const senderNext    = applyTradeToPlayer(sender,    trade.fromOffer, trade.toOffer);
    const recipientNext = applyTradeToPlayer(recipient, trade.toOffer,   trade.fromOffer);

    await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${sender.id}`, senderNext);
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/players/${recipient.id}`, recipientNext);
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/trades/${tradeId}`, {
      status: 'accepted', resolvedAt: Date.now(),
    });
  }, []);

  // ── Challenges (1v1 minigames) ───────────────────────────────────────────
  const proposeChallenge = useCallback(async (record) => {
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/challenges`, record);
  }, []);

  const acceptChallenge = useCallback(async (challengeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`, {
      status: 'active', acceptedAt: Date.now(),
    });
  }, []);

  const declineChallenge = useCallback(async (challengeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`, {
      status: 'declined', resolvedAt: Date.now(),
    });
  }, []);

  const cancelChallenge = useCallback(async (challengeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`, {
      status: 'cancelled', resolvedAt: Date.now(),
    });
  }, []);

  const challengesRef = useRef(challenges);
  useEffect(() => { challengesRef.current = challenges; }, [challenges]);

  // Submit my score to a challenge. Whichever side I am (from/to), set my
  // score field. If both sides have submitted afterward, compute winner +
  // mark completed.
  const submitChallengeScore = useCallback(async (challengeId, myPlayerId, score) => {
    const ch = challengesRef.current[challengeId];
    if (!ch || ch.status !== 'active') return;
    const isFrom = ch.fromPlayerId === myPlayerId;
    const otherScore = isFrom ? ch.toScore : ch.fromScore;
    const updates = isFrom ? { fromScore: score } : { toScore: score };

    if (otherScore != null) {
      // Both have submitted now. Dispatch to the game's winner logic, with
      // the framework applying a stat-based tiebreaker on true ties. The
      // tiebreaker reason is preserved so the result UI can badge it.
      const fromScore = isFrom ? score       : ch.fromScore;
      const toScore   = isFrom ? ch.toScore  : score;
      const outcome = resolveChallengeOutcome(ch.gameId, fromScore, toScore, ch.fromStat, ch.toStat);
      updates.status     = 'completed';
      updates.winner     = outcome.winner;
      updates.tiebreaker = outcome.tiebreaker;
      updates.resolvedAt = Date.now();
    }

    await updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`, updates);
  }, []);

  // ── Shop purchase ────────────────────────────────────────────────────────
  // Atomically deducts coins (preferring smaller denominations) and adds the
  // item using strict-match stacking (so a player's custom same-named item
  // isn't clobbered). Returns { ok, reason? }.
  const purchaseFromShop = useCallback(async (playerId, shopId, itemName) => {
    const player = playersRef.current[playerId];
    if (!player) return { ok: false, reason: 'No player record' };

    const shopItem = describeShopItem(shopId, itemName);
    if (!shopItem) return { ok: false, reason: 'Item not in shop' };

    // Encumbrance check — would this push the player past STR × 15 × size?
    const str       = player.abilityScores?.str ?? 10;
    const size      = player.sizeCategory || 'medium';
    const cap       = carryCapacity(str, size);
    const curWeight = currentWeight(player.inventory);
    const itemWt    = shopItem.weightLb ?? 0;
    if (curWeight + itemWt > cap) {
      return { ok: false, reason: `Too heavy (would push to ${(curWeight + itemWt).toFixed(1)} lb / ${cap} lb cap)` };
    }

    // Affordability — sum copper.
    const totalCp = totalInCp(player.currency);
    if (totalCp < shopItem.priceCp) {
      return { ok: false, reason: 'Not enough coin' };
    }

    // Compute the actual coin spend + any change.
    const split = cpToCoins(shopItem.priceCp, player.currency || {});
    if (!split) return { ok: false, reason: 'Coin breakdown failed' };

    // Apply transaction to the in-memory player record.
    const nextCurrency  = applyCoinTransaction(player.currency || {}, split.coinsToSpend, split.changeCp);
    const inventoryItem = {
      category:    shopItem.category,
      name:        shopItem.name,
      description: shopItem.description || '',
      qty:         1,
      weightLb:    shopItem.weightLb ?? 0,
      _extra: {
        ...(shopItem.weapon ? { weapon: shopItem.weapon } : {}),
        ...(shopItem.armor  ? { armor:  shopItem.armor  } : {}),
        ...(shopItem.use    ? { use:    shopItem.use    } : {}),
      },
    };
    const nextInventory = addItemStrict(player.inventory || [], inventoryItem);

    await writeDoc(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, {
      inventory: nextInventory,
      currency:  nextCurrency,
    });
    return { ok: true };
  }, []);

  // ── DM item grant ────────────────────────────────────────────────────────
  // Give any catalog item to a player — no coin, no encumbrance block (the
  // DM's call overrides carry limits; the UI still shows the weight). Same
  // strict-match stacking and structured-field copy as shop purchases.
  const grantItem = useCallback(async (playerId, itemName) => {
    const player = playersRef.current[playerId];
    if (!player) return { ok: false, reason: 'No player record' };

    const cat = catalogLookup(itemName);
    if (!cat) return { ok: false, reason: 'Item not in catalog' };

    const inventoryItem = {
      category:    cat.category,
      name:        cat.name,
      description: cat.description || '',
      qty:         1,
      weightLb:    cat.weightLb ?? 0,
      _extra: {
        ...(cat.weapon ? { weapon: cat.weapon } : {}),
        ...(cat.armor  ? { armor:  cat.armor  } : {}),
        ...(cat.use    ? { use:    cat.use    } : {}),
      },
    };
    const nextInventory = addItemStrict(player.inventory || [], inventoryItem);

    await writeDoc(`campaigns/${CAMPAIGN_ID}/players/${playerId}`, {
      inventory: nextInventory,
    });
    return { ok: true };
  }, []);

  // Race-safe finalizer. When both sides submit scores within the same
  // Firestore round-trip, neither submitChallengeScore call sees the other's
  // score yet, so neither flips status to 'completed'. Once the snapshot
  // arrives with both scores present, any client can finalize — the outcome
  // is deterministic, so concurrent writes are idempotent.
  const finalizingRef = useRef(new Set());
  useEffect(() => {
    for (const ch of Object.values(challenges || {})) {
      if (!ch?.id) continue;
      if (ch.status !== 'active') continue;
      if (ch.fromScore == null || ch.toScore == null) continue;
      if (finalizingRef.current.has(ch.id)) continue;
      finalizingRef.current.add(ch.id);
      const outcome = resolveChallengeOutcome(ch.gameId, ch.fromScore, ch.toScore, ch.fromStat, ch.toStat);
      updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${ch.id}`, {
        status: 'completed',
        winner: outcome.winner,
        tiebreaker: outcome.tiebreaker,
        resolvedAt: Date.now(),
      }).catch(() => {
        finalizingRef.current.delete(ch.id);
      });
    }
  }, [challenges]);

  // Mark a completed challenge as acknowledged so the banner clears for
  // everyone. Shared dismissal is fine because both players have to have
  // seen the result by the time either one closes the overlay.
  const dismissChallenge = useCallback(async (challengeId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`, {
      dismissedAt: Date.now(),
    });
  }, []);

  // DM convenience — clear a stuck/abandoned challenge.
  const deleteChallenge = useCallback(async (challengeId) => {
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/challenges/${challengeId}`);
  }, []);

  // ── Hidden Discovery: traps, perception prompts, trap reactions ────────

  const trapsRef               = useRef(traps);
  const perceptionPromptsRef   = useRef(perceptionPrompts);
  const trapReactionsRef       = useRef(trapReactions);
  const tokensRef              = useRef(tokens);
  const teleportsRef           = useRef(teleports);
  useEffect(() => { trapsRef.current = traps; }, [traps]);
  useEffect(() => { perceptionPromptsRef.current = perceptionPrompts; }, [perceptionPrompts]);
  useEffect(() => { trapReactionsRef.current = trapReactions; }, [trapReactions]);
  useEffect(() => { tokensRef.current = tokens; }, [tokens]);
  useEffect(() => { teleportsRef.current = teleports; }, [teleports]);
  // playersRef already exists higher up in the file (line ~674) for trades.

  const createTrap = useCallback(async (record) => {
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/traps`, {
      discoveryDC: null,
      reactionDC:  12,
      damageDie:   '1d6',
      damageType:  'piercing',
      secondaryEffect: null,
      triggerType: 'enter',
      triggered:   false,
      triggeredBy: null,
      discoveredBy: {},
      createdAt:   Date.now(),
      ...record,
    });
  }, []);

  const updateTrap = useCallback(async (trapId, patch) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/traps/${trapId}`, patch);
  }, []);

  const deleteTrap = useCallback(async (trapId) => {
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/traps/${trapId}`);
  }, []);

  // Mark a trap triggered + open the reaction window. Reveals to everyone.
  // For player-owned tokens, opens a 3-second reaction prompt.
  // For DM-owned tokens (monsters/NPCs), broadcasts full damage immediately.
  const triggerTrap = useCallback(async (trapId, tokenId) => {
    const trap  = trapsRef.current[trapId];
    const token = tokensRef.current[tokenId];
    if (!trap || trap.triggered) return null;
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/traps/${trapId}`, {
      triggered:   true,
      triggeredBy: tokenId,
    });

    const ownerPlayerId = token?.ownerId || null;
    if (ownerPlayerId) {
      // Player owns this token — open the reaction window.
      return await addToCollection(`campaigns/${CAMPAIGN_ID}/trapReactions`, {
        trapId,
        tokenId,
        ownerPlayerId,
        deadlineTs: Date.now() + 3000,
        choice:    null,
        outcome:   null,
        rollTotal: null,
        createdAt: Date.now(),
      });
    }

    // Monster / NPC — no reaction prompt; broadcast full damage directly.
    if (trap.damageDie) {
      const parts = String(trap.damageDie).match(/(\d+)d(\d+)/i);
      const count = parts ? parseInt(parts[1], 10) : 1;
      const sides = parts ? parseInt(parts[2], 10) : 6;
      const dmg   = rollDice({ dice: [{ sides, count }], modifier: 0, mode: 'normal' });
      await addToCollection(`campaigns/${CAMPAIGN_ID}/rolls`, {
        ...dmg,
        playerId:   null,
        playerName: trap.label || 'Trap',
        colour:     '#c0392b',
        isDm:       true,
        label:      `${trap.label || 'Trap'} (no reaction)`,
        createdAt:  Date.now(),
      });
    }
    return null;
  }, []);

  // Apply a player's reaction choice (or timeout) — resolves the d20 vs DC,
  // broadcasts a labelled dice roll, applies position changes.
  const submitTrapReaction = useCallback(async (reactionId, choice) => {
    const reaction = trapReactionsRef.current[reactionId];
    if (!reaction || reaction.outcome) return;
    const trap = trapsRef.current[reaction.trapId];
    if (!trap) return;
    const token = tokensRef.current[reaction.tokenId];
    const player = reaction.ownerPlayerId ? playersRef.current[reaction.ownerPlayerId] : null;

    // Stat map for the four reaction types.
    const STAT_FOR = { dodge: 'dex', jump: 'str', brace: 'con', sense: 'wis' };
    const DC_OFFSET = { dodge: 0, jump: 2, brace: -3, sense: 4 };

    let outcome  = 'timeout';
    let rollTotal = null;
    if (choice && STAT_FOR[choice]) {
      const stat = STAT_FOR[choice];
      const score = player?.abilityScores?.[stat] ?? 10;
      const statMod = Math.floor((score - 10) / 2);
      const d20    = 1 + Math.floor(Math.random() * 20);
      rollTotal = d20 + statMod;
      const effectiveDC = (trap.reactionDC ?? 12) + (DC_OFFSET[choice] ?? 0);
      outcome = rollTotal >= effectiveDC ? 'success' : 'fail';
    }

    await updateDocFields(`campaigns/${CAMPAIGN_ID}/trapReactions/${reactionId}`, {
      choice: choice || null,
      outcome,
      rollTotal,
      resolvedAt: Date.now(),
    });

    // Damage broadcast.
    let damageTaken = 0;
    if (trap.damageDie) {
      const success = outcome === 'success';
      const half    = choice === 'brace' && success;
      const dmg     = rollDice({ dice: [{ sides: parseInt(trap.damageDie.split('d')[1], 10) || 6, count: parseInt(trap.damageDie.split('d')[0], 10) || 1 }], modifier: 0, mode: 'normal' });
      let total     = dmg.total;
      if (half) total = Math.floor(total / 2);
      if (success && !half) total = 0; // dodge/jump/sense success = no damage
      damageTaken = total;
      const reactionLabel = choice ? `${choice}${outcome === 'success' ? ' ✓' : ' ✗'}` : 'no reaction';
      await addToCollection(`campaigns/${CAMPAIGN_ID}/rolls`, {
        ...dmg,
        total,
        playerId:   reaction.ownerPlayerId,
        playerName: trap.label || 'Trap',
        colour:     '#c0392b',
        isDm:       false,
        label:      reactionLabel,
        createdAt:  Date.now(),
      });
    }
    // Stash the damage on the reaction doc so the result screen can show it
    // without re-reading the broadcast roll.
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/trapReactions/${reactionId}`, {
      damageTaken,
    });

    // Position changes — Jump pushes forward 1 cell, Dodge shifts 1 cell
    // (DM-chosen) — in v1 we randomize the dodge shift to a free cardinal.
    if (outcome === 'success' && token) {
      if (choice === 'jump') {
        // Push 1 cell past the trap in the direction of travel.
        const dx = Math.sign(trap.x - token.x);
        const dy = Math.sign(trap.y - token.y);
        if (dx !== 0 || dy !== 0) {
          await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${reaction.tokenId}`, {
            x: trap.x + dx,
            y: trap.y + dy,
          });
        }
      } else if (choice === 'dodge') {
        // Pick a random adjacent free cell — v1 simple, DM can fine-tune.
        const candidates = [[0,-1],[1,0],[0,1],[-1,0]];
        const [ddx, ddy] = candidates[Math.floor(Math.random() * candidates.length)];
        await updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${reaction.tokenId}`, {
          x: token.x + ddx,
          y: token.y + ddy,
        });
      }
    }
  }, []);

  // DM-side resolve for monster tokens — no reaction prompt, DM picks directly.
  // Same resolve flow, but operates on a fake reaction with no owner.
  const resolveMonsterTrapReaction = useCallback(async (trapId, tokenId, choice) => {
    const trap = trapsRef.current[trapId];
    const token = tokensRef.current[tokenId];
    if (!trap || !token) return;
    // Synthesize a transient reaction doc and process it.
    const id = await addToCollection(`campaigns/${CAMPAIGN_ID}/trapReactions`, {
      trapId, tokenId, ownerPlayerId: null,
      deadlineTs: Date.now() + 1, choice: null, outcome: null, rollTotal: null,
      createdAt: Date.now(),
    });
    await submitTrapReaction(id, choice);
  }, [submitTrapReaction]);

  // Open a perception prompt. Targets all live players by default.
  const createPerceptionPrompt = useCallback(async (targetPlayerIds = null) => {
    const ids = targetPlayerIds || Object.keys(playersRef.current).filter((pid) => {
      const p = playersRef.current[pid];
      return p && !p.deletedAt;
    });
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/perceptionPrompts`, {
      createdAt: Date.now(),
      targetPlayerIds: ids,
      responses: {},
      resolved:  false,
    });
  }, []);

  // Player posts their roll into a prompt. Idempotent — running twice with the
  // same playerId is a no-op.
  // doorsRef would be needed but doors live in MapContext; we'll piggyback by
  // accepting a `secretDoors` list from the caller (PerceptionPromptBanner).
  const submitPerceptionResponse = useCallback(async (promptId, playerId, roll, isPassive, secretDoors = []) => {
    const prompt = perceptionPromptsRef.current[promptId];
    if (!prompt || prompt.resolved) return;
    if (prompt.responses?.[playerId]) return;

    // Find this player's token to anchor sight.
    const viewerToken = Object.values(tokensRef.current).find((t) => t.ownerId === playerId);
    if (!viewerToken) {
      await updateDocFields(`campaigns/${CAMPAIGN_ID}/perceptionPrompts/${promptId}`, {
        [`responses.${playerId}`]: { roll, isPassive, ts: Date.now() },
      });
      return;
    }

    const stealthedTokens = Object.values(tokensRef.current)
      .filter((t) => (t.buffs || []).some((b) => b.id === 'stealthed') && t.stealthDC != null);

    const { trapIds, doorIds, tokenIds } = resolveDiscoveries({
      viewerToken: { x: viewerToken.x, y: viewerToken.y },
      roll,
      isPassive,
      playerId,
      traps:         Object.values(trapsRef.current),
      secretDoors,
      stealthedTokens,
    });

    // Hidden trapdoor teleports — same discovery model as traps/doors. A
    // teleport has two endpoints; either can satisfy the sight check.
    const range = isPassive ? 2 : 12;
    const teleportIds = [];
    for (const tp of Object.values(teleportsRef.current)) {
      if (!tp.hidden) continue;
      if (tp.discoveryDC == null) continue;
      if (tp.discoveredBy?.[playerId]) continue;
      if (roll < tp.discoveryDC) continue;
      const dA = Math.max(Math.abs(tp.a.x - viewerToken.x), Math.abs(tp.a.y - viewerToken.y));
      const dB = Math.max(Math.abs(tp.b.x - viewerToken.x), Math.abs(tp.b.y - viewerToken.y));
      if (Math.min(dA, dB) > range) continue;
      teleportIds.push(tp.id);
    }

    const stamp = Date.now();
    await Promise.all([
      ...trapIds.map((id) =>
        updateDocFields(`campaigns/${CAMPAIGN_ID}/traps/${id}`, {
          [`discoveredBy.${playerId}`]: stamp,
        })),
      ...tokenIds.map((id) =>
        updateDocFields(`campaigns/${CAMPAIGN_ID}/tokens/${id}`, {
          [`discoveredBy.${playerId}`]: stamp,
        })),
      ...teleportIds.map((id) =>
        updateDocFields(`campaigns/${CAMPAIGN_ID}/teleports/${id}`, {
          [`discoveredBy.${playerId}`]: stamp,
        })),
      // Doors live in the map doc — the caller (PerceptionPromptBanner) writes
      // door discoveries via MapContext.revealSecretDoor since the map schema
      // is opaque to CampaignContext.
      updateDocFields(`campaigns/${CAMPAIGN_ID}/perceptionPrompts/${promptId}`, {
        [`responses.${playerId}`]: { roll, isPassive, ts: stamp, discoveredDoors: doorIds },
      }),
    ]);
    return { trapIds, doorIds, tokenIds, teleportIds };
  }, []);

  const resolvePerceptionPrompt = useCallback(async (promptId) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/perceptionPrompts/${promptId}`, {
      resolved: true,
    });
  }, []);

  // ── Teleports (same-map, two-click pen) ────────────────────────────────
  const createTeleport = useCallback(async (record) => {
    return await addToCollection(`campaigns/${CAMPAIGN_ID}/teleports`, {
      twoWay: true,
      style:  'portal',
      label:  null,
      hidden: false,
      discoveryDC:  null,
      discoveredBy: {},
      createdAt:    Date.now(),
      ...record,
    });
  }, []);

  const updateTeleport = useCallback(async (teleportId, patch) => {
    await updateDocFields(`campaigns/${CAMPAIGN_ID}/teleports/${teleportId}`, patch);
  }, []);

  const deleteTeleport = useCallback(async (teleportId) => {
    await deleteDocument(`campaigns/${CAMPAIGN_ID}/teleports/${teleportId}`);
  }, []);

  return (
    <CampaignContext.Provider value={{
      campaign, tokens, players, initiative, musicState, pings, diceRolls, trades, challenges,
      traps, perceptionPrompts, trapReactions,
      spawnToken, cloneToken, moveToken, updateToken, removeToken, assignToken, updatePlayer, deletePlayer, restorePlayerSnapshot,
      equipItemInInventory, useConsumable,
      startRolling, submitRoll, beginBattle,
      nextTurn, prevTurn, saveInitiative, saveMusicState,
      addToInitiative, removeFromInitiative, clearInitiative, cancelInitiative,
      createPing, deletePing,
      submitDiceRoll, deleteDiceRoll, dismissDiceRoll,
      proposeTrade, acceptTrade, declineTrade, cancelTrade,
      proposeChallenge, acceptChallenge, declineChallenge, cancelChallenge, submitChallengeScore, dismissChallenge, deleteChallenge,
      createTrap, updateTrap, deleteTrap, triggerTrap, submitTrapReaction, resolveMonsterTrapReaction,
      createPerceptionPrompt, submitPerceptionResponse, resolvePerceptionPrompt,
      teleports, createTeleport, updateTeleport, deleteTeleport,
      purchaseFromShop, grantItem,
    }}>
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const ctx = useContext(CampaignContext);
  if (!ctx) throw new Error('useCampaign must be inside CampaignProvider');
  return ctx;
}
